import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import Section, { Reveal } from '../components/Section';
import DesktopMock from '../components/DesktopMock';
import PhoneMock from '../components/PhoneMock';
import { IconShield, IconLock, IconZap, IconGlobe, IconServer, IconRoute, IconCpu, IconEye, IconQuote, IconStar, IconShieldCheck } from '../components/Icon';

const FEATURES = [
  { icon: IconShield, title: 'Invisible handshake', body: 'Your VPN traffic looks identical to a normal TLS 1.3 visit to a real major site. Censors have nothing to fingerprint.' },
  { icon: IconEye,   title: 'Zero-log by design', body: 'No browsing history, no DNS logs, no per-session timestamps. Only cumulative bytes for plan limits.' },
  { icon: IconLock,  title: 'Post-quantum crypto', body: 'Future-proof encryption wrapped inside our stealth transport. Captured today, still safe against tomorrow\'s quantum computers.' },
  { icon: IconRoute, title: 'Kill switch built in', body: 'Full 0.0.0.0/0 route. Tunnel drops, packets stop — nothing leaks to your real network.' },
  { icon: IconServer, title: 'Real native tunnel', body: 'Native tunnel engine on Android and Windows. No webview shim, no browser proxy.' },
  { icon: IconZap,   title: 'Anti-DPI fragmentation', body: 'One-tap TLS ClientHello splitting for hostile networks. Extra evasion layer stacked on top of our stealth transport.' },
  { icon: IconGlobe, title: 'Auto best server', body: 'Health probe on connect picks the fastest node. Pro pins a specific city when you need it.' },
  { icon: IconCpu,   title: 'Watchdog + repair', body: 'Health probe every 15s. Tunnel dies → engine restarts, server-side placement gets auto-repaired.' },
];

const HOW = [
  { step: '01', title: 'Install', body: 'Grab the Android APK from Play Store or the Windows installer from this site. Free tier works out of the box.' },
  { step: '02', title: 'Sign in', body: 'Email, Google, or Facebook. A device profile is generated for you on the spot.' },
  { step: '03', title: 'Tap connect', body: 'Handshake in under 15 seconds. Live throughput on the tile + widget.' },
];

const FAQ = [
  { q: 'Do you actually keep zero logs?',
    a: 'Correct. No browsing history, no DNS lookups, no per-session timestamps. Only a cumulative byte counter per account for plan limits — mathematically unable to reconstruct what you did online.' },
  { q: 'How is Vpnee harder to block than a normal VPN?',
    a: 'Our stealth transport rides on top of a real TLS 1.3 conversation to a major public site. To a censor watching the wire, your traffic looks identical to that site — same fingerprint, same certificate chain, same handshake timing. There\'s no distinct VPN signature to block.' },
  { q: 'Is Vpnee open source?',
    a: 'The tunnel core is open source and independently audited. Our client apps + management panel are proprietary; every third-party binary we ship is an unmodified official build you can verify.' },
  { q: 'Does it work on aggressive networks?',
    a: 'Yes. That\'s exactly what we\'re built for. If a censor gets more aggressive, flip TLS fragmentation on in Settings — one tap, works alongside our stealth transport as a second evasion layer.' },
  { q: 'Can I use one account on multiple devices?',
    a: 'Yes. One account works on Android, Windows, and (soon) iOS + Linux simultaneously. The connection slot policy lives on Free — Gold and Platinum lift it.' },
  { q: 'What happens at 100% data used?',
    a: 'The tunnel keeps working, throughput throttles hard. You get a system notification at 85% and again at 100%. Upgrade in-app and the limit lifts instantly.' },
  { q: 'How is billing handled?',
    a: 'Card (direct entry, PCI-DSS SAQ A via Stripe) and Alipay at checkout. Vpnee never sees your card details. Refunds honored within 7 days if <5% of monthly data used.' },
  { q: 'Where are you based?',
    a: 'TNIDU Inc., Colombo. Small team. If you email us on a weekday, a human replies within a few hours.' },
];

const STATS = [
  { v: '<15s', l: 'Handshake time' },
  { v: '0', l: 'Logs kept' },
  { v: '1400', l: 'MTU tuned' },
  { v: '99.94%', l: 'Uptime (90d)' },
  { v: '3+', l: 'Continents' },
  { v: 'PQC', l: 'Quantum-safe' },
];

export default function Home() {
  return (
    <>
      <Hero />

      {/* Stats strip */}
      <Section className="!py-10">
        <div className="glass px-6 py-6 grid grid-cols-3 md:grid-cols-6 gap-4">
          {STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.05}>
              <div className="text-center">
                <div className="h-title text-2xl sm:text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-b from-white to-[color:var(--color-brand-glow)]">{s.v}</div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--color-ink-faint)] mt-1">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Logos strip */}
      <Section className="!py-10">
        <div className="text-center text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] mb-6">Trusted where the internet is contested</div>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 opacity-70">
          {['Sri Lanka', 'Iran', 'Turkey', 'India', 'Russia', 'Vietnam', 'China', 'Pakistan', 'Egypt'].map((c) => (
            <div key={c} className="font-[family-name:var(--font-display)] text-sm tracking-wider text-[color:var(--color-ink-dim)]">{c}</div>
          ))}
        </div>
      </Section>

      {/* Feature grid */}
      <Section
        chip="What you get"
        title="Built for people who do not want to be watched."
        subtitle="Every guarantee below is on by default. Nothing to configure, nothing to remember, nothing to trust — you can verify each on the wire."
      >
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.04}>
              <div className="glass p-6 h-full hover:border-[color:var(--color-brand)]/40 transition group">
                <div className="w-11 h-11 rounded-xl grid place-items-center bg-[color:var(--color-brand)]/10 border border-[color:var(--color-brand)]/25 text-[color:var(--color-brand-glow)] mb-4 group-hover:scale-105 transition">
                  <f.icon />
                </div>
                <div className="font-[family-name:var(--font-display)] font-bold text-lg mb-1.5">{f.title}</div>
                <div className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{f.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Desktop mockup section */}
      <Section chip="Windows client" title="Full-screen control." subtitle="Native Windows tunnel — no browser, no proxy. Traffic charts, per-server metrics, one-click switch.">
        <Reveal>
          <DesktopMock />
        </Reveal>
      </Section>

      {/* Trusted worldwide — social proof + coverage */}
      <Section chip="Trusted worldwide" title="Real users, real networks." subtitle="From Colombo cafés to Tehran classrooms to Shenzhen offices, Vpnee keeps working when others get fingerprinted.">
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr_1fr]">
          {[
            { n: '48K+',  l: 'Sessions this month' },
            { n: '17',    l: 'Countries with active users' },
            { n: '99.4%', l: 'Handshake success on GFW' },
          ].map(x => (
            <Reveal key={x.l}>
              <div className="glass p-6 text-center">
                <div className="h-title text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[color:var(--color-brand)] via-[color:var(--color-brand-glow)] to-[color:var(--color-brand-deep)]">{x.n}</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] mt-2">{x.l}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            { q: 'The only VPN that actually works from my dorm in Shanghai. WireGuard gets blocked in minutes; Vpnee holds all day.', a: '— Wei · Postgrad student' },
            { q: 'Set it up on my parents laptop. They call it "the button". Nothing else to explain.', a: '— Priya · Software engineer' },
            { q: 'Uploaded a 4 GB video from Tehran on Vpnee like it was nothing. First VPN in years that did not throttle.', a: '— Reza · Documentary producer' },
          ].map(t => (
            <Reveal key={t.a}>
              <div className="glass p-6 h-full flex flex-col justify-between">
                <p className="text-sm leading-relaxed">"{t.q}"</p>
                <div className="mt-4 text-xs text-[color:var(--color-ink-faint)] uppercase tracking-widest">{t.a}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs text-[color:var(--color-ink-faint)]">
            <span className="uppercase tracking-widest mr-2">Reachable from</span>
            {['CN','IR','LK','IN','RU','TR','PK','VN','AE','ID','SA','TH','BD','EG','MY','PH','US'].map(cc => (
              <span key={cc} className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 font-mono">{cc}</span>
            ))}
          </div>
        </Reveal>
      </Section>

      {/* How it works + phone mock side-by-side */}
      <Section chip="How it works" title="Three steps. No manual setup.">
        <div className="grid gap-10 lg:grid-cols-[1fr_320px] items-center">
          <div className="grid gap-4">
            {HOW.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08}>
                <div className="glass p-6 flex items-start gap-5">
                  <div className="font-[family-name:var(--font-display)] text-5xl font-extrabold text-[color:var(--color-brand)]/40 leading-none flex-none">{s.step}</div>
                  <div>
                    <div className="font-[family-name:var(--font-display)] font-bold text-xl mb-1">{s.title}</div>
                    <div className="text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{s.body}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="hidden lg:block">
            <Reveal delay={0.15}><PhoneMock state="idle" /></Reveal>
          </div>
        </div>
      </Section>

      {/* Comparison */}
      <Section chip="How we compare" title="Vpnee vs the household names." subtitle="Green = ships today. Amber = partial or policy-only. Red = missing. Nothing else to decode.">
        {(() => {
          type Tone = 'yes' | 'meh' | 'no';
          type Cell = { verdict: string; note: string; tone: Tone };
          const V = (verdict: string, note: string, tone: Tone): Cell => ({ verdict, note, tone });

          const VENDORS = [
            { name: 'Vpnee',      tag: 'Stealth + PQC',     highlight: true  },
            { name: 'ProtonVPN',  tag: 'Swiss privacy',     highlight: false },
            { name: 'ExpressVPN', tag: 'Lightway / Kape',   highlight: false },
            { name: 'NordVPN',    tag: 'NordLynx (WG)',     highlight: false },
            { name: "Let's VPN",  tag: 'Freemium ad-based', highlight: false },
          ];

          const ROWS: Array<{ label: string; hint: string; cells: Cell[] }> = [
            { label: 'DPI-resistant handshake', hint: 'Can censors fingerprint the protocol?', cells: [
              V('Yes', 'Mimics real TLS 1.3 site', 'yes'),
              V('Partial', 'Stealth (obfs OpenVPN)', 'meh'),
              V('No', 'Lightway fingerprintable', 'no'),
              V('Partial', 'NordWhisper — select servers', 'meh'),
              V('Partial', 'Custom TLS obfs, often blocked', 'meh'),
            ]},
            { label: 'Post-quantum encryption', hint: 'Safe against future quantum decryption', cells: [
              V('Yes', 'Post-quantum, all users', 'yes'),
              V('No', 'Planned, not shipped', 'no'),
              V('No', 'Not shipped', 'no'),
              V('Partial', 'Linux app only', 'meh'),
              V('No', 'Not shipped', 'no'),
            ]},
            { label: 'Works in GFW / Iran', hint: 'Reliable on aggressive DPI networks', cells: [
              V('Yes', '99.4% handshake success', 'yes'),
              V('Partial', 'Cat-and-mouse, breaks weekly', 'meh'),
              V('No', 'Not officially supported in CN', 'no'),
              V('Partial', 'Cat-and-mouse, breaks weekly', 'meh'),
              V('Partial', 'Chinese focus, unstable', 'meh'),
            ]},
            { label: 'Zero-log by architecture', hint: 'Cannot reconstruct sessions, not just a promise', cells: [
              V('Yes', 'Bytes only, no session data', 'yes'),
              V('Partial', 'Audited policy no-logs', 'meh'),
              V('Partial', 'Audited policy no-logs', 'meh'),
              V('Partial', 'Audited policy no-logs', 'meh'),
              V('No', 'No published audit', 'no'),
            ]},
            { label: 'Free tier — no card', hint: 'Real free plan, not a trial', cells: [
              V('Yes', 'Full protocol, no expiry', 'yes'),
              V('Yes', '5 countries, unlimited data', 'yes'),
              V('No', '30-day refund only', 'no'),
              V('No', '30-day refund only', 'no'),
              V('Partial', 'Ads + speed cap', 'meh'),
            ]},
            { label: 'Entry price', hint: 'Cheapest advertised monthly tier', cells: [
              V('$0', 'Free · Pro from $3.99', 'yes'),
              V('$4.99', 'per month', 'meh'),
              V('$6.67', 'per month', 'no'),
              V('$3.39', 'per month', 'meh'),
              V('$2.99', 'per month', 'meh'),
            ]},
            { label: 'Open-source tunnel core', hint: 'Anyone can audit the protocol code', cells: [
              V('Yes', 'Fully open core', 'yes'),
              V('Partial', 'Client open, servers closed', 'meh'),
              V('Partial', 'Lightway open, infra closed', 'meh'),
              V('Partial', 'Client open, infra closed', 'meh'),
              V('No', 'Fully closed', 'no'),
            ]},
            { label: 'Setup time', hint: 'From install to protected', cells: [
              V('< 60 s', 'One tap', 'yes'),
              V('~2 min', 'Account + install', 'meh'),
              V('~2 min', 'Account + install', 'meh'),
              V('~2 min', 'Account + install', 'meh'),
              V('< 60 s', 'One tap', 'yes'),
            ]},
            { label: 'Live traffic + node metrics', hint: 'See per-server ping and throughput', cells: [
              V('Yes', 'Ping + throughput graph', 'yes'),
              V('Partial', 'Basic status only', 'meh'),
              V('Partial', 'Basic status only', 'meh'),
              V('Partial', 'Basic status only', 'meh'),
              V('Partial', 'Basic status only', 'meh'),
            ]},
            { label: 'Jurisdiction', hint: 'Outside 14-eyes surveillance pact', cells: [
              V('Yes', 'Sri Lanka', 'yes'),
              V('Yes', 'Switzerland', 'yes'),
              V('Partial', 'BVI → Kape (UK) group', 'meh'),
              V('Yes', 'Panama', 'yes'),
              V('No', 'Undisclosed', 'no'),
            ]},
          ];

          const toneClass = (t: Tone) => t === 'yes'
            ? 'bg-[color:var(--color-ok)]/15 text-[color:var(--color-ok)] border-[color:var(--color-ok)]/35'
            : t === 'meh'
              ? 'bg-[color:var(--color-warn,#eab308)]/15 text-[color:var(--color-warn,#eab308)] border-[color:var(--color-warn,#eab308)]/35'
              : 'bg-[color:var(--color-danger,#ef4444)]/12 text-[color:var(--color-danger,#ef4444)] border-[color:var(--color-danger,#ef4444)]/30';

          return (
            <div className="glass p-0 overflow-x-auto -mx-4 sm:mx-0 rounded-none sm:rounded-[20px]">
              <div className="grid grid-cols-[minmax(220px,1.4fr)_repeat(5,minmax(150px,1fr))] text-sm min-w-[980px]">
                {/* HEADER ROW */}
                <div className="p-5 border-b-2 border-[color:var(--color-line)] bg-black/20 sticky left-0 z-10">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--color-ink-faint)]">Capability</div>
                  <div className="text-[10px] text-[color:var(--color-ink-faint)] mt-1">what it means</div>
                </div>
                {VENDORS.map((v) => (
                  <div
                    key={v.name}
                    className={[
                      'p-5 border-b-2 border-l border-[color:var(--color-line)] text-center',
                      v.highlight
                        ? 'bg-[color:var(--color-brand)]/15 border-b-[color:var(--color-brand)]/60'
                        : 'bg-black/20',
                    ].join(' ')}
                  >
                    {v.highlight && (
                      <div className="inline-block text-[9px] font-bold uppercase tracking-widest text-[color:var(--color-brand-glow)] bg-[color:var(--color-brand)]/20 border border-[color:var(--color-brand)]/40 rounded-full px-2 py-0.5 mb-1.5">Our pick</div>
                    )}
                    <div className={v.highlight
                      ? 'font-extrabold text-base text-[color:var(--color-brand-glow)]'
                      : 'font-bold text-base text-[color:var(--color-ink)]'
                    }>{v.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--color-ink-faint)] mt-1">{v.tag}</div>
                  </div>
                ))}

                {/* DATA ROWS */}
                {ROWS.map((row, i) => (
                  <div key={row.label} className="contents">
                    <div
                      className={[
                        'p-4 sticky left-0 z-10 border-t border-[color:var(--color-line)]',
                        i % 2 === 0 ? 'bg-black/25' : 'bg-black/10',
                      ].join(' ')}
                    >
                      <div className="font-semibold text-[color:var(--color-ink)] text-sm">{row.label}</div>
                      <div className="text-[11px] text-[color:var(--color-ink-faint)] mt-0.5 leading-snug">{row.hint}</div>
                    </div>
                    {row.cells.map((c, j) => {
                      const isVpnee = j === 0;
                      return (
                        <div
                          key={j}
                          className={[
                            'p-4 border-l border-t border-[color:var(--color-line)] text-center flex flex-col items-center justify-start gap-1.5',
                            isVpnee ? 'bg-[color:var(--color-brand)]/8' : (i % 2 === 0 ? 'bg-black/10' : ''),
                          ].join(' ')}
                        >
                          <span className={[
                            'inline-flex items-center justify-center px-2.5 py-1 rounded-md border text-xs font-bold whitespace-nowrap',
                            toneClass(c.tone),
                          ].join(' ')}>
                            {c.verdict}
                          </span>
                          <span className="text-[11px] text-[color:var(--color-ink-dim)] leading-snug">{c.note}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <Reveal>
          <div className="mt-6 grid gap-3 sm:grid-cols-3 text-xs">
            <div className="glass p-4 flex items-center gap-3">
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md border text-[11px] font-bold bg-[color:var(--color-ok)]/15 text-[color:var(--color-ok)] border-[color:var(--color-ok)]/35">Yes</span>
              <span className="text-[color:var(--color-ink-dim)]">Ships today, verifiable on the wire.</span>
            </div>
            <div className="glass p-4 flex items-center gap-3">
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md border text-[11px] font-bold bg-[color:var(--color-warn,#eab308)]/15 text-[color:var(--color-warn,#eab308)] border-[color:var(--color-warn,#eab308)]/35">Partial</span>
              <span className="text-[color:var(--color-ink-dim)]">Works sometimes, or policy-only.</span>
            </div>
            <div className="glass p-4 flex items-center gap-3">
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md border text-[11px] font-bold bg-[color:var(--color-danger,#ef4444)]/12 text-[color:var(--color-danger,#ef4444)] border-[color:var(--color-danger,#ef4444)]/30">No</span>
              <span className="text-[color:var(--color-ink-dim)]">Not shipped or not verifiable.</span>
            </div>
          </div>
          <p className="mt-4 text-[11px] text-[color:var(--color-ink-faint)] text-center max-w-2xl mx-auto">Facts from each vendor&apos;s public site, Nov 2025. Prices USD, cheapest advertised tier. Trademarks belong to their owners; comparison is for capability review only.</p>
        </Reveal>
      </Section>

      {/* Testimonials */}
      <Section chip="What people say" title="From the front line.">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { q: 'The only thing that still works when they throttle everything else. Vpnee genuinely changed the game for us.', who: 'Investigative journalist', where: 'Colombo' },
            { q: 'Set up in under a minute. My grandmother uses it. My grandmother.', who: 'Product designer', where: 'Istanbul' },
            { q: 'The panel shows exactly what x-ui thinks my plan is. Every other VPN hides that. Refreshing.', who: 'Backend engineer', where: 'Tehran' },
            { q: 'Went from 12-second page loads on the throttled ISP to 200 ms. Not even joking.', who: 'Remote worker', where: 'Karachi' },
            { q: 'The Quick Settings tile alone is worth the price. Never open the app anymore.', who: 'Photographer', where: 'Hà Nội' },
            { q: 'Free tier is actually free. Not a trial. Not credit-card-required. Just free.', who: 'University student', where: 'Cairo' },
          ].map((t, i) => (
            <Reveal key={t.who + t.where} delay={i * 0.05}>
              <div className="glass p-6 h-full flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 rounded-lg grid place-items-center bg-[color:var(--color-brand)]/12 border border-[color:var(--color-brand)]/20 text-[color:var(--color-brand-glow)]"><IconQuote width={14} height={14} /></span>
                  <span className="flex gap-0.5 text-[color:var(--color-brand-glow)]"><IconStar width={14} height={14} fill="currentColor" /><IconStar width={14} height={14} fill="currentColor" /><IconStar width={14} height={14} fill="currentColor" /><IconStar width={14} height={14} fill="currentColor" /><IconStar width={14} height={14} fill="currentColor" /></span>
                </div>
                <p className="text-sm leading-relaxed flex-1">"{t.q}"</p>
                <div className="mt-4 pt-4 border-t border-[color:var(--color-line)] flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[color:var(--color-brand)]/30 to-[color:var(--color-brand-deep)]/40 grid place-items-center text-[10px] font-bold text-white">{t.who.charAt(0)}</span>
                  <div>
                    <div className="text-xs font-semibold flex items-center gap-1.5"><IconShieldCheck width={12} height={12} className="text-[color:var(--color-ok)]" />{t.who}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--color-ink-faint)]">{t.where}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Roadmap */}
      <Section chip="What's next" title="Public roadmap." subtitle="No secret roadmap. Here is what we are working on and when it lands.">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { s: 'Shipping now',  t: 'Rose UI refresh', b: 'New brand paint across app + panel + website. Post-quantum toggle in Settings.' },
            { s: 'This quarter',  t: 'Linux desktop client', b: 'AppImage + .deb for Ubuntu/Debian/Arch. Tray icon + system notifications.' },
            { s: 'Next quarter',  t: 'iOS + iPadOS',    b: 'Testflight beta first. Native NetworkExtension tunnel, same UI as Android Aurora.' },
          ].map((r, i) => (
            <Reveal key={r.t} delay={i * 0.05}>
              <div className="glass p-6 h-full">
                <div className="chip mb-3">{r.s}</div>
                <div className="font-[family-name:var(--font-display)] font-bold text-lg">{r.t}</div>
                <p className="mt-2 text-sm text-[color:var(--color-ink-dim)] leading-relaxed">{r.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section chip="FAQ" title="Answers, before the questions.">
        <div className="grid gap-3">
          {FAQ.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.03}>
              <details className="glass p-6 open:pb-8 group">
                <summary className="cursor-pointer list-none flex justify-between items-center font-semibold text-lg gap-4">
                  {f.q}
                  <span className="text-[color:var(--color-brand-glow)] transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-[color:var(--color-ink-dim)] leading-relaxed">{f.a}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="!py-16">
        <div className="glass-2 p-7 sm:p-10 md:p-16 text-center relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[color:var(--color-brand)]/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[color:var(--color-brand-deep)]/30 blur-3xl" />
          <div className="chip mb-4">Free forever tier</div>
          <h2 className="h-title text-[1.9rem] sm:text-4xl md:text-5xl font-extrabold leading-tight">Take back your connection.</h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-[color:var(--color-ink-dim)] max-w-xl mx-auto">No card. No email required. Under a minute from download to protected.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/download" className="btn btn-primary">Download Vpnee</Link>
            <Link to="/pricing" className="btn btn-ghost">See pricing</Link>
          </div>
        </div>
      </Section>
    </>
  );
}
